import { useState, useEffect } from "react";
import { Loader2, CheckCircle2, Download, MessageCircle, X, ShieldCheck, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, waLink, CONTACT, formatApiErrorDetail } from "@/lib/api";
import { generateReceiptPDF } from "@/lib/receipt";
import { DateTimeFields } from "@/components/site/DateTimeFields";
import { trackEvent } from "@/analytics";

const field = "w-full h-12 rounded-xl border border-border bg-background px-3 outline-none focus:border-primary text-base";
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

// earliest selectable date (36h ahead)
const minDate = () => {
  const d = new Date(Date.now() + 36 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
};

// latest selectable date (1 month ahead)
const maxDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return d.toISOString().slice(0, 10);
};

export function BookingDialog({ open, onClose, persons, items, total }) {
  const [step, setStep] = useState("form");
  const [busy, setBusy] = useState(false);
  const [booking, setBooking] = useState(null);
  const [avail, setAvail] = useState(null);
  const [locating, setLocating] = useState(false);
  const [fullDates, setFullDates] = useState([]);
  const [form, setForm] = useState({
    name: "", phone: "", address: "", pincode: "", event_date: minDate(), event_time: "19:00",
    without_onion_garlic: false, cooking_instructions: "",
    geo_lat: null, geo_lng: null, location_link: "",
  });

  useEffect(() => {
    if (!open || !form.event_date) return;
    setAvail(null);
    api.get(`/availability/${form.event_date}`).then((r) => setAvail(r.data)).catch(() => setAvail(null));
  }, [open, form.event_date]);

  // Fetch fully-booked / closed dates for the current + next month so the
  // calendar greys them out before the customer even taps a date.
  useEffect(() => {
    if (!open) return;
    const now = new Date();
    const months = [0, 1].map((off) => {
      const d = new Date(now.getFullYear(), now.getMonth() + off, 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    });
    Promise.all(months.map((m) => api.get(`/availability/month/${m}`)))
      .then((rs) => setFullDates(rs.flatMap((r) => r.data?.full_dates || [])))
      .catch(() => {});
  }, [open]);

  const soldOut = avail && avail.available <= 0;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setStep("form"); setBooking(null); setBusy(false); };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Location not supported on this device"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const link = `https://www.google.com/maps?q=${latitude},${longitude}`;
        setForm((f) => ({ ...f, geo_lat: latitude, geo_lng: longitude, location_link: link }));
        setLocating(false);
        toast.success("Location pinned for delivery");
      },
      () => { setLocating(false); toast.error("Couldn't get your location. Please allow location access."); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleClose = () => { onClose(); setTimeout(reset, 300); };

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name";
    if (!/^\d{10}$/.test(form.phone.trim())) return "Enter a valid 10-digit phone number";
    if (!form.address.trim()) return "Please enter your address";
    if (!/^208\d{3}$/.test(form.pincode.trim())) return "Enter a valid Kanpur pincode (starts with 208)";
    if (!form.event_date || !form.event_time) return "Please choose event date & time";
    const dt = new Date(`${form.event_date}T${form.event_time}`);
    if (dt.getTime() < Date.now() + 36 * 3600 * 1000) return "Bookings must be at least 36 hours in advance";
    const limit = new Date(); limit.setMonth(limit.getMonth() + 1); limit.setHours(23, 59, 59, 999);
    if (dt.getTime() > limit.getTime()) return "Bookings can be made only up to 1 month in advance";
    return null;
  };

  const pay = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/bookings/create-order", {
        ...form, phone: form.phone.trim(), persons, items,
      });
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment. Check your connection."); setBusy(false); return; }
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name: "The Quisine",
        description: "Bulk order advance (₹500)",
        prefill: { name: form.name, contact: form.phone },
        theme: { color: "#124f31" },
        handler: async (resp) => {
          try {
            const v = await api.post("/bookings/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setBooking(v.data);
            localStorage.setItem("tq_last_booking", v.data.id);
            trackEvent("booking_confirmed", {
              value: v.data.advance_paid,
              currency: "INR",
              receipt_no: v.data.receipt_no,
              persons: v.data.persons,
            });
            setStep("done");
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed");
          } finally { setBusy(false); }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.on("payment.failed", (resp) => {
        setBusy(false);
        const reason = resp?.error?.description || "Your payment could not be completed";
        toast.error(
          `${reason}. If any amount was debited, it is not captured by us and your bank/Razorpay will auto-refund it within 5-7 working days. Please try again.`,
          { duration: 9000 }
        );
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start payment");
      setBusy(false);
    }
  };

  const ownerMessage = booking
    ? `New Bulk Order — ${booking.receipt_no}\n` +
      `Name: ${booking.name}\nPhone: ${booking.phone}\nAddress: ${booking.address}, Kanpur ${booking.pincode || ""}\n` +
      (booking.location_link ? `Map: ${booking.location_link}\n` : "") +
      `Persons: ${booking.persons}\nEvent: ${booking.event_date} @ ${booking.event_time}\n` +
      `Preference: ${booking.without_onion_garlic ? "Without Onion & Garlic" : "Regular"}\n` +
      `Menu: ${(booking.items || []).map((i) => i.dish).join(", ")}\n` +
      (booking.cooking_instructions ? `Instructions: ${booking.cooking_instructions}\n` : "") +
      `Total: ${inr(booking.total_amount)} | Advance paid: ${inr(booking.advance_paid)} | Balance: ${inr(booking.balance_due)}`
    : "";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto p-0 gap-0 max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:max-w-none max-sm:rounded-t-3xl max-sm:rounded-b-none max-sm:border-x-0 max-sm:border-b-0 max-sm:max-h-[94vh] max-sm:data-[state=open]:slide-in-from-bottom-4 max-sm:data-[state=closed]:slide-out-to-bottom-4 max-sm:data-[state=open]:slide-in-from-left-0 max-sm:data-[state=closed]:slide-out-to-left-0 max-sm:data-[state=open]:slide-in-from-top-0 max-sm:data-[state=closed]:slide-out-to-top-0 max-sm:pb-safe">
        <DialogTitle className="sr-only">Book your bulk food order</DialogTitle>
        <DialogDescription className="sr-only">Pay a ₹500 advance to confirm your bulk food order for delivery in Kanpur.</DialogDescription>
        {step === "form" && (
          <div className="p-6">
            <h3 className="font-serif text-3xl text-primary tracking-tight">Book your order</h3>
            <p className="text-sm text-muted-foreground mt-1">Pay a ₹500 advance to confirm. Balance is collected later.</p>

            <div className="mt-4 rounded-xl bg-secondary/50 p-3 text-sm flex items-center justify-between">
              <span className="text-muted-foreground">{persons} persons · advance</span>
              <span className="font-serif text-2xl text-primary">₹500</span>
            </div>

            <div className="mt-4 space-y-3">
              <input data-testid="booking-name" className={field} placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} />
              <input data-testid="booking-phone" className={field} placeholder="Phone (10 digits)" inputMode="numeric" maxLength={10} value={form.phone} onChange={(e) => set("phone", e.target.value.replace(/\D/g, ""))} />
              <textarea data-testid="booking-address" className={`${field} h-20 py-2`} placeholder="Delivery address (house/flat, street, locality)" value={form.address} onChange={(e) => set("address", e.target.value)} />
              <input data-testid="booking-pincode" className={field} placeholder="Delivery pincode (208xxx)" inputMode="numeric" maxLength={6} value={form.pincode} onChange={(e) => set("pincode", e.target.value.replace(/\D/g, ""))} />
              <button type="button" data-testid="booking-use-location" onClick={useMyLocation} disabled={locating}
                className="w-full h-12 rounded-xl border border-primary/40 text-primary text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                {form.geo_lat ? "Location pinned ✓ (tap to update)" : "Use my current location"}
              </button>
              {form.location_link && (
                <a data-testid="booking-location-link" href={form.location_link} target="_blank" rel="noreferrer" className="block text-xs text-primary underline underline-offset-2 text-center">View pinned delivery location on map</a>
              )}
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground">City</label>
                  <input data-testid="booking-city" className={`${field} mt-1 bg-secondary/40`} value="Kanpur" disabled readOnly />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground">State</label>
                  <input data-testid="booking-state" className={`${field} mt-1 bg-secondary/40`} value="Uttar Pradesh" disabled readOnly />
                </div>
              </div>
              <DateTimeFields
                date={form.event_date}
                time={form.event_time}
                minDate={minDate()}
                maxDate={maxDate()}
                bookedOutDates={fullDates}
                onDateChange={(v) => set("event_date", v)}
                onTimeChange={(v) => set("event_time", v)}
              />
              <p className="text-xs text-muted-foreground">Book between 36 hours and 1 month in advance.</p>
              <p className="text-xs text-muted-foreground">Bookings open 36 hours ahead and up to 1 month in advance.</p>
              {avail && (
                soldOut
                  ? <p data-testid="booking-availability" className="text-xs font-medium text-destructive">Fully booked for this date — please choose another day.</p>
                  : <p data-testid="booking-availability" className="text-xs font-medium text-primary">{avail.available} slot{avail.available > 1 ? "s" : ""} left for this date.</p>
              )}

              <label className="flex items-center justify-between rounded-xl border border-border p-3 cursor-pointer">
                <span className="text-sm font-medium">Without onion & garlic</span>
                <input data-testid="booking-jain" type="checkbox" className="w-5 h-5 accent-[#124f31]" checked={form.without_onion_garlic} onChange={(e) => set("without_onion_garlic", e.target.checked)} />
              </label>
              <textarea data-testid="booking-instructions" className={`${field} h-16 py-2`} placeholder="Cooking instructions (optional)" value={form.cooking_instructions} onChange={(e) => set("cooking_instructions", e.target.value)} />
            </div>

            <button data-testid="booking-pay-btn" onClick={pay} disabled={busy || soldOut}
              className="mt-5 h-14 w-full rounded-full bg-primary text-primary-foreground font-medium grid place-items-center disabled:opacity-60">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> {soldOut ? "Fully booked" : "Pay ₹500 & Confirm Booking"}</span>}
            </button>
            <p className="mt-2 text-center text-xs text-muted-foreground">Secure payment via Razorpay</p>
          </div>
        )}

        {step === "done" && booking && (
          <div className="p-6 text-center">
            <div className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-primary/10 text-primary">
              <CheckCircle2 className="w-9 h-9" />
            </div>
            <h3 className="font-serif text-3xl text-primary mt-4">Booking confirmed!</h3>
            <p data-testid="booking-receipt-no" className="text-sm text-muted-foreground mt-1">Receipt {booking.receipt_no}</p>

            <div className="mt-5 text-left rounded-2xl border border-border p-4 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Persons</span><span>{booking.persons}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Event</span><span>{booking.event_date} @ {booking.event_time}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span>{inr(booking.total_amount)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Advance paid</span><span className="text-primary font-medium">{inr(booking.advance_paid)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Balance due</span><span className="font-medium">{inr(booking.balance_due)}</span></div>
            </div>

            <a data-testid="booking-whatsapp-confirm" href={waLink(ownerMessage)} target="_blank" rel="noreferrer"
              className="mt-5 h-13 py-3.5 w-full rounded-full bg-[#25D366] text-white font-medium grid place-items-center">
              <span className="flex items-center gap-2"><MessageCircle className="w-5 h-5" /> Send booking to us on WhatsApp</span>
            </a>
            <button data-testid="booking-download-pdf" onClick={() => generateReceiptPDF(booking)}
              className="mt-3 h-13 py-3.5 w-full rounded-full border border-primary text-primary font-medium grid place-items-center">
              <span className="flex items-center gap-2"><Download className="w-5 h-5" /> Download Receipt (PDF)</span>
            </button>
            <a href={`/booking/${booking.id}`} className="mt-3 inline-block text-sm text-primary underline underline-offset-4">View my booking</a>
          </div>
        )}

        <button onClick={handleClose} className="absolute top-3 right-3 grid place-items-center w-9 h-9 rounded-full hover:bg-secondary" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
      </DialogContent>
    </Dialog>
  );
}
